import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import BatchUploader from './SharedUploadSession';
import useDetachedUploadRefresh from '../hooks/useDetachedUploadRefresh';
import { useUpload } from '../contexts/UploadContext';
import PageDropZone from './PageDropZone';
import ShareManager from './ShareManager';
import TagSelectorModal from './TagSelectorModal';
import MobilePhotoSwiper from './MobilePhotoSwiper';
import PhotoViewerImage from './PhotoViewerImage';
import { FiArrowLeft, FiHeart, FiTag, FiUpload, FiPlus, FiCheckSquare, FiGrid, FiRefreshCw, FiInfo, FiX, FiTrash2, FiChevronDown, FiLink, FiShare2, FiImage, FiChevronLeft, FiChevronRight, FiRotateCw, FiMinus, FiPlus as FiPlusIcon, FiMaximize2, FiMinimize2, FiDownload, FiUsers, FiType } from 'react-icons/fi';
import AlbumPermissions from './AlbumPermissions';
import { FaHeart } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { SkeletonPhotoGrid, SkeletonText } from './Skeleton';
import { showToast } from './NotificationTray';
import useIsMobile from '../utils/useIsMobile';
import { buildVariantUrl, getPhotoVersion, photoCache } from '../utils/photoCache';
import { cacheStrategy } from '../utils/cacheStrategy';
import { getPrivateCacheScope } from '../utils/privateCacheScope.js';
import { useAuth } from '../contexts/AuthContext';
import { getFeatureFlags } from '../utils/featureFlags';
import { useDragSelect } from '../hooks/useLassoSelection';
import { isProcessingLike } from '../utils/processingState';

export default function AlbumDetail() {
  const uploadContext = useUpload();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const features = getFeatureFlags(user);
  const stripEnabled = features.strip;
  const sharesEnabled = features.shares;
  const [data, setData] = useState(null);
  const [order, setOrder] = useState([]);
  const [showUploader, setShowUploader] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [allPhotos, setAllPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photosPage, setPhotosPage] = useState(1);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const photosPerPage = 50;
  const authToken = React.useMemo(() => {
    try {
      return window?.localStorage?.getItem('auth_token');
    } catch {
      return null;
    }
  }, []);
  const tokenParam = ''; // Same-origin authenticated session cookie.
  const getOriginalUrl = (image, download = false) => {
    if (!image?.filename) return null;
    const suffix = download ? `${tokenParam ? '&' : '?'}download=1` : '';
    return `/media/originals/${image.filename}${tokenParam}${suffix}`;
  };
  // Build simplified EXIF for UI from exif_data
  const buildUiExif = (img) => {
    try {
      const raw = img?.exif_data ? (typeof img.exif_data === 'string' ? JSON.parse(img.exif_data) : img.exif_data) : null;
      if (!raw || typeof raw !== 'object') return null;
      const camera = raw.Make && raw.Model ? `${raw.Make} ${raw.Model}`.replace(/^(\w+)\s+\1\s+/, '$1 ') : (raw.Model || raw.Make || null);
      const lens = raw.LensModel || null;
      const iso = raw.ISO || null;
      const aperture = raw.FNumber ? parseFloat(raw.FNumber).toFixed(1) : null;
      const focal = raw.FocalLength ? Math.round(parseFloat(raw.FocalLength)) : null;
      let shutter = null;
      if (raw.ExposureTime) {
        const t = parseFloat(raw.ExposureTime);
        shutter = isFinite(t) ? (t >= 1 ? `${t}` : `1/${Math.round(1 / Math.max(t, 1e-6))}`) : null;
      }
      const takenAt = raw.DateTimeOriginal || raw.DateTime || img?.taken_at || null;
      return { camera, lens, aperture, shutter_speed: shutter, iso, focal_length: focal, taken_at: takenAt };
    } catch { return null; }
  };

  const [droppedFiles, setDroppedFiles] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOver, setDraggedOver] = useState(null);
  const [isRearranging, setIsRearranging] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [albumTitle, setAlbumTitle] = useState('');
  const [albumDescription, setAlbumDescription] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [viewMode, setViewMode] = useState('single');
  const [showBatchRename, setShowBatchRename] = useState(false);
  const [basename, setBasename] = useState('');
  const [startNumber, setStartNumber] = useState(1);
  const [digitCount, setDigitCount] = useState(3);
  const [subscription, setSubscription] = useState(null);
  const [canUseBatchRename, setCanUseBatchRename] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePhotosOption, setDeletePhotosOption] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const isMobile = useIsMobile();
  const isViewerCompact = useIsMobile(1024);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMobileViewer, setShowMobileViewer] = useState(false);
  const [mobileViewerIndex, setMobileViewerIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchStartTime, setTouchStartTime] = useState(null);
  const [showImageInfo, setShowImageInfo] = useState(false);
  const [showStripShareModal, setShowStripShareModal] = useState(false);
  const [stripShareCaption, setStripShareCaption] = useState('');
  const [sharingToStrip, setSharingToStrip] = useState(false);
  const [showTeamPermissions, setShowTeamPermissions] = useState(false);
  const cacheUserId = getPrivateCacheScope().scope;

  // Zoom and pan state (matching Photos page)
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const [rotating, setRotating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const lightboxRef = useRef(null);

  // Drag-to-select
  const gridRef = useRef(null);
  const { lassoRef, isDragSelecting, startDrag } = useDragSelect({
    containerRef: gridRef,
    onSelectionChange: setSelectedImages,
    enabled: selectionMode && !isRearranging && !isMobile,
  });

  async function loadAlbum(forceRefresh = false) {
    if (!forceRefresh && user && cacheUserId) {
      const cached = await cacheStrategy.get('albumImages', { id, userId: cacheUserId });
      if (cached?.data) {
        const cachedAlbum = cached.data;
        setData(cachedAlbum);
        const cachedImages = Array.isArray(cachedAlbum.images) ? cachedAlbum.images : [];
        setOrder(cachedImages.map(i => i.id));
        setAlbumTitle(cachedAlbum.album?.title || '');
        setAlbumDescription(cachedAlbum.album?.description || '');
        setLoading(false);
        if (cached.fresh) return;
      }
    }

    // Keep mounted upload results intact while refreshing an already loaded album.
    if (!data) setLoading(true);
    try {
      // Add cache-busting param and no-cache headers when forcing refresh
      const url = forceRefresh ? `/api/albums/${id}?_t=${Date.now()}` : `/api/albums/${id}`;
      const response = await fetch(url, {
        credentials: 'include',
        ...(forceRefresh ? { headers: { 'Cache-Control': 'no-cache, no-store', 'Pragma': 'no-cache' } } : {}),
      });
      if (!response.ok) {
        console.error('Album fetch failed', response.status, response.statusText);
        throw new Error(`Album fetch failed: ${response.status}`);
      }
      const albumData = await response.json();
      setData(albumData);
      const albumImages = Array.isArray(albumData.images) ? albumData.images : [];
      setOrder(albumImages.map(i => i.id));
      setAlbumTitle(albumData.album.title);
      setAlbumDescription(albumData.album.description || '');
      if (user && cacheUserId) cacheStrategy.set('albumImages', albumData, { id, userId: cacheUserId });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAlbum();
    checkSubscription();
  }, [id, user?.id]);

  // Poll processing status for any in-flight tiles (mirrors ImageGrid). This hits the
  // uncached /processing/status endpoint directly and updates tile statuses in place,
  // so spinners clear as soon as the variant worker finishes, independent of the
  // cached album fetch. Stops automatically once nothing is processing.
  const processingImageIds = React.useMemo(
    () => (data?.images || []).filter((img) => isProcessingLike(img)).map((img) => img.id).filter(Boolean),
    [data?.images]
  );
  useEffect(() => {
    if (!processingImageIds.length) return () => {};
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch('/api/images/processing/status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ image_ids: processingImageIds })
        });
        if (!res.ok) return;
        const payload = await res.json();
        if (cancelled) return;
        const statuses = payload.images || {};
        const changed = processingImageIds.some((imageId) => {
          const s = statuses[imageId];
          return s?.status && s.status !== 'processing' && s.status !== 'queued' && s.status !== 'pending';
        });
        setData((prev) => {
          if (!prev?.images) return prev;
          let dirty = false;
          const nextImages = prev.images.map((img) => {
            const s = statuses[img.id];
            if (!s?.status || s.status === img.processing_status) return img;
            dirty = true;
            const next = { ...img, processing_status: s.status };
            if (s.status === 'ready') next.processing_error = null;
            else if (s.error) next.processing_error = s.error;
            return next;
          });
          return dirty ? { ...prev, images: nextImages } : prev;
        });
        // Keep the IndexedDB album cache from re-serving stale spinners on navigation.
        if (changed && user) cacheStrategy.invalidate('albumImages', { id, userId: cacheUserId });
      } catch {
        // ignore polling errors; next tick retries
      }
    };

    poll();
    const t = setInterval(poll, 1500);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [processingImageIds.join(',')]);

  // Load tags when showing tag selector
  useEffect(() => {
    if (showTagSelector) {
      fetch('/api/tags', { credentials: 'include' })
        .then(r => r.json())
        .then(setAvailableTags)
        .catch(console.error);
    }
  }, [showTagSelector]);

  async function checkSubscription() {
    try {
      const res = await fetch('/api/subscription', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
        setCanUseBatchRename(data.limits?.canUseBatchRename || false);
      }
    } catch (e) {
      console.error('Failed to check subscription:', e);
    }
  }

  useEffect(() => {
    if (showPhotoPicker) {
      // Get all photos to properly calculate available photos
      const albumImageIds = data?.images.map(img => img.id) || [];
      
      // For pagination, we need to get all photos first to filter properly
      // This is not ideal for large collections, but works for now
      fetch(`/api/images?limit=1000&fields=compact&variants=sm,md`, { credentials: 'include' })
        .then(r => r.json())
        .then(response => {
          // Handle new paginated response format
          const allPhotosData = response.images || response;
          
          // Filter out photos already in album
          const availablePhotos = allPhotosData.filter(p => !albumImageIds.includes(p.id));
          
          // Calculate pagination for available photos only
          const totalAvailable = availablePhotos.length;
          const startIndex = (photosPage - 1) * photosPerPage;
          const endIndex = startIndex + photosPerPage;
          const paginatedPhotos = availablePhotos.slice(startIndex, endIndex);
          
          setAllPhotos(paginatedPhotos);
          setTotalPhotos(totalAvailable);
        })
        .catch(err => {
          console.error('Failed to load photos:', err);
          setAllPhotos([]);
          setTotalPhotos(0);
        });
    }
  }, [showPhotoPicker, data, photosPage]);

  async function saveOrder(newOrder = order) {
    await fetch(`/api/albums/${id}/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: newOrder }),
      credentials: 'include'
    });
    cacheStrategy.invalidate('addToAlbum', { userId: cacheUserId, albumId: id });
    // Don't reload, just update the state
    if (newOrder !== order) {
      setOrder(newOrder);
    }
  }

  // Toggle like/favorite for an image
  async function toggleLike(imageId, currentlyLiked) {
    const newFavoriteState = !currentlyLiked;
    
    // Update local state immediately for responsive UI
    setData(prev => ({
      ...prev,
      images: prev.images.map(img => 
        img.id === imageId ? { ...img, is_favorite: newFavoriteState } : img
      )
    }));
    
    try {
      await fetch(`/api/images/${imageId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_favorite: newFavoriteState })
      });
      cacheStrategy.invalidate(newFavoriteState ? 'like' : 'unlike', {
        userId: cacheUserId,
        imageId,
        albumId: id
      });
    } catch (error) {
      console.error('Failed to toggle like:', error);
      // Revert on error
      setData(prev => ({
        ...prev,
        images: prev.images.map(img => 
          img.id === imageId ? { ...img, is_favorite: currentlyLiked } : img
        )
      }));
    }
  }

  async function toggleHidden(imageId, currentlyHidden) {
    const newHiddenState = !currentlyHidden;
    setData(prev => ({
      ...prev,
      images: prev.images.map(img =>
        img.id === imageId ? { ...img, hidden_from_library: newHiddenState ? 1 : 0 } : img
      )
    }));
    try {
      const res = await fetch(`/api/images/${imageId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ hidden_from_library: newHiddenState })
      });
      if (!res.ok) throw new Error(`PATCH failed: ${res.status}`);
    } catch (error) {
      console.error('Failed to toggle hidden:', error);
      setData(prev => ({
        ...prev,
        images: prev.images.map(img =>
          img.id === imageId ? { ...img, hidden_from_library: currentlyHidden ? 1 : 0 } : img
        )
      }));
    }
  }

  // Drag and drop handlers
  function handleDragStart(e, index) {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e, index) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedItem === null) return;
    if (draggedItem === index) return;
    
    setDraggedOver(index);
  }

  function handleDragLeave() {
    setDraggedOver(null);
  }

  function handleDrop(e, dropIndex) {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDraggedOver(null);
      return;
    }

    // Reorder the array
    const newOrder = [...order];
    const draggedId = newOrder[draggedItem];
    
    // Remove dragged item
    newOrder.splice(draggedItem, 1);
    
    // Insert at new position
    newOrder.splice(dropIndex, 0, draggedId);
    
    setOrder(newOrder);
    setDraggedItem(null);
    setDraggedOver(null);
    
    // Auto-save the new order
    saveOrder(newOrder);
  }

  function handleDragEnd() {
    setDraggedItem(null);
    setDraggedOver(null);
  }

  async function updateAlbumInfo() {
    await fetch(`/api/albums/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        title: albumTitle, 
        description: albumDescription 
      }),
      credentials: 'include'
    });
    setEditMode(false);
    loadAlbum();
  }

  async function deleteAlbum() {
    setShowDeleteDialog(true);
  }

  async function confirmDeleteAlbum() {
    const hiddenCount = data?.images?.filter(img => img.hidden_from_library)?.length || 0;
    let unhidePhotos = false;
    if (hiddenCount > 0 && !deletePhotosOption) {
      unhidePhotos = confirm(`This album has ${hiddenCount} hidden photo${hiddenCount !== 1 ? 's' : ''} that won't appear anywhere after deletion. Show them in All Photos?`);
    }
    await fetch(`/api/albums/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ deletePhotos: deletePhotosOption, unhidePhotos })
    });
    navigate('/albums');
  }

  async function batchRenamePhotos() {
    if (!basename) {
      alert('Please enter a base name');
      return;
    }
    
    try {
      // Rename all images in the album with sequential numbers
      const updates = data.images.map((img, index) => {
        const number = String(startNumber + index).padStart(digitCount, '0');
        return {
          id: img.id,
          title: `${basename}${number}`
        };
      });

      // Send batch update to server
      await fetch(`/api/albums/${id}/batch-rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ updates })
      });

      setShowBatchRename(false);
      setBasename('');
      setStartNumber(1);
      loadAlbum();
    } catch (error) {
      console.error('Failed to batch rename:', error);
      alert('Failed to rename photos. Please try again.');
    }
  }

  async function removeFromAlbum(imageId) {
    if (!confirm('Remove this photo from the album?')) return;
    // Optimistically update UI
    setData(prev => ({ ...prev, images: prev.images.filter(img => img.id !== imageId) }));
    setOrder(prev => prev.filter(imgId => imgId !== imageId));

    // Fire request in background
    fetch(`/api/albums/${id}/images/${imageId}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    .then(async (response) => {
      if (!response.ok) {
        try { console.error('Failed to remove image from album:', await response.json()); } catch {}
      } else {
        const result = await response.json();
        cacheStrategy.invalidate('removeFromAlbum', { userId: cacheUserId, albumId: id, imageId });
        if (result.hidden_orphan) {
          const unhide = confirm('This photo is hidden from All Photos and is no longer in any album. Show it in All Photos?');
          if (unhide) {
            fetch(`/api/images/${imageId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ hidden_from_library: false })
            }).catch(() => {});
          }
        }
      }
    })
    .catch((error) => {
      console.error('Error removing image from album:', error);
    });
  }

  async function addSelectedPhotos() {
    const imageIds = selectedPhotos.map((photoId) => String(photoId));
    if (imageIds.length === 0) return;

    try {
      const response = await fetch(`/api/albums/${id}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_ids: imageIds })
      });

      if (!response.ok) {
        let message = 'Failed to add photos to album.';
        try {
          const data = await response.json();
          if (data?.message) message = data.message;
        } catch {}
        throw new Error(message);
      }

      setSelectedPhotos([]);
      setShowPhotoPicker(false);
      setPhotosPage(1);
      cacheStrategy.invalidate('addToAlbum', { userId: cacheUserId, albumId: id });
      loadAlbum();
    } catch (error) {
      console.error('Failed to add photos to album:', error);
      alert(error.message || 'Failed to add photos to album. Please try again.');
    }
  }

  function handleUploadComplete(counts) {
    cacheStrategy.invalidate('upload', { userId: cacheUserId, albumId: id });
    cacheStrategy.invalidate('albumImages', { id, userId: cacheUserId });

    // Keep per-file processing and failure results visible until explicitly dismissed.

    // Refresh immediately to show processing spinners
    loadAlbum(true);

    if (counts.hasProcessing || counts.success > 0) {
      showToast('Photos uploaded', 'Thumbnails are generating and will appear as they finish.', 'info');
      // The processing-status poll effect (above) takes over from here: it watches every
      // in-flight tile via the uncached /processing/status endpoint and clears spinners
      // the moment each image is ready. (The old 5s loadAlbum loop re-fetched the cached
      // album endpoint, which could serve a frozen snapshot, and gave up after 2 min.)
    }
  }

  useDetachedUploadRefresh(detail => String(detail.albumId) === String(id), counts => handleUploadComplete(counts));

  function handleFilesDropped(files) {
    if (uploadContext?.uploading) return;
    setDroppedFiles(files);
    setShowUploader(true);
  }

  function togglePhotoSelection(photoId) {
    setSelectedPhotos(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  }

  function toggleImageSelection(imageId) {
    setSelectedImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  }

  async function deleteSelectedImages() {
    if (!confirm(`Remove ${selectedImages.length} photos from this album? (Does not delete files)`)) return;

    // Optimistically update UI
    const ids = [...selectedImages];
    setData(prev => ({ ...prev, images: prev.images.filter(img => !ids.includes(img.id)) }));
    setOrder(prev => prev.filter(imgId => !ids.includes(imgId)));
    setSelectionMode(false);
    setSelectedImages([]);

    // Fire requests in parallel
    Promise.allSettled(ids.map(imageId => fetch(`/api/albums/${id}/images/${imageId}`, {
      method: 'DELETE',
      credentials: 'include'
    }))).then(() => {
      // Optionally refresh in background to reconcile
      cacheStrategy.invalidate('removeFromAlbum', { userId: cacheUserId, albumId: id });
      loadAlbum();
    }).catch((error) => {
      console.error('Failed to remove some photos from album:', error);
      loadAlbum();
    });
  }

  async function addTagToSelected(tagId) {
    try {
      for (const imageId of selectedImages) {
        await fetch(`/api/images/${imageId}/tags`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ tag_id: tagId })
        });
      }
      showToast(`Added tag to ${selectedImages.length} photos`, '', 'success');
      setSelectionMode(false);
      setSelectedImages([]);
      setShowTagSelector(false);
      loadAlbum(); // Reload to show updated tags
    } catch (error) {
      console.error('Failed to add tag:', error);
      showToast('Failed to add tag to photos', '', 'error');
    }
  }

  // Touch event handlers for mobile tap detection
  const handleTouchStart = (e, index) => {
    if (selectionMode || isRearranging || !isViewerCompact) return;
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    setTouchStartTime(Date.now());
  };

  const handleTouchEnd = (e, index) => {
    if (selectionMode || isRearranging || !isViewerCompact) return;
    if (!touchStart || !touchStartTime) return;

    const touchEnd = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    const deltaX = Math.abs(touchEnd.x - touchStart.x);
    const deltaY = Math.abs(touchEnd.y - touchStart.y);
    const deltaTime = Date.now() - touchStartTime;

    // Consider it a tap if movement is less than 10px and time is less than 300ms
    if (deltaX < 10 && deltaY < 10 && deltaTime < 300) {
      e.preventDefault();
      openLightbox(data.images.find(img => img.id === order[index]), index);
    }

    setTouchStart(null);
    setTouchStartTime(null);
  };

  async function hydrateImageDetails(image) {
    if (!image) return image;
    const needsTags = typeof image.tags === 'undefined';
    const needsExif = typeof image.exif_data === 'undefined';
    const needsVariants = !image._cdn_full && (!image.cdn_urls || !image.cdn_urls.xl);

    if (!needsTags && !needsExif && !needsVariants) return image;

    const params = new URLSearchParams();
    const include = [];
    if (needsTags) include.push('tags');
    if (needsExif) include.push('exif');
    if (include.length) params.set('include', include.join(','));
    if (needsVariants) params.set('variants', 'sm,md,lg,xl,original');

    try {
      const url = params.toString()
        ? `/api/images/${image.id}?${params.toString()}`
        : `/api/images/${image.id}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return image;
      const data = await res.json();
      // Fetching metadata does not change the photo bytes.
      const bump = getPhotoVersion(image);
      const merged = { ...image, ...data, cdn_urls: { ...image.cdn_urls, ...data.cdn_urls }, _cdn_full: needsVariants ? true : image._cdn_full, _v: bump };
      setData((prev) => ({
        ...prev,
        images: prev.images.map((img) => img.id === image.id ? { ...img, ...data, cdn_urls: merged.cdn_urls, _cdn_full: merged._cdn_full, _v: bump } : img)
      }));
      return merged;
    } catch (error) {
      console.error('Failed to fetch image details:', error);
      return image;
    }
  }

  function withPreview(image) {
    const tile = document.querySelector(`[data-image-id="${CSS.escape(String(image.id))}"] img`);
    return { ...image, _v: getPhotoVersion(image), _previewSrc: tile?.complete && tile.naturalWidth ? tile.currentSrc || tile.src : undefined };
  }

  async function openLightbox(image, index) {
    image = withPreview(image);
    if (isViewerCompact) {
      setMobileViewerIndex(index);
      setShowMobileViewer(true);
    } else {
      setLightboxImage(image);
      setLightboxIndex(index);
      const hydrated = await hydrateImageDetails(image);
      setLightboxImage((prev) => (prev?.id === image.id ? hydrated : prev));
    }
  }

  function closeLightbox() {
    setLightboxImage(null);
    setEditingTitle(false);
    setEditTitle('');
  }

  function startEditingTitle() {
    setEditingTitle(true);
    setEditTitle(lightboxImage.title || lightboxImage.filename || '');
  }

  function cancelEditTitle() {
    setEditingTitle(false);
    setEditTitle('');
  }

  async function saveTitle() {
    try {
      const response = await fetch(`/api/images/${lightboxImage.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: editTitle })
      });

      if (response.ok) {
        // Update local state
        const updatedImages = data.images.map(img => 
          img.id === lightboxImage.id ? { ...img, title: editTitle } : img
        );
        setData({ ...data, images: updatedImages });
        setLightboxImage({ ...lightboxImage, title: editTitle });
        setEditingTitle(false);
        setEditTitle('');
      }
    } catch (error) {
      console.error('Failed to update title:', error);
    }
  }

  async function navigateLightbox(direction) {
    let newIndex = lightboxIndex + direction;
    
    // Handle wrapping
    if (newIndex < 0) {
      newIndex = data.images.length - 1;
    } else if (newIndex >= data.images.length) {
      newIndex = 0;
    }
    
    const newImage = withPreview(data.images[newIndex]);
    setLightboxImage(newImage);
    setLightboxIndex(newIndex);
    const hydrated = await hydrateImageDetails(newImage);
    setLightboxImage((prev) => (prev?.id === newImage?.id ? hydrated : prev));
    // Reset zoom/pan when navigating
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }

  // Zoom functions (matching Photos page)
  function applyZoom(delta, center) {
    const newZoom = Math.max(0.5, Math.min(5, zoom + delta));
    setZoom(newZoom);
    if (newZoom === 1) {
      setPan({ x: 0, y: 0 });
    }
  }

  function resetZoomPan() {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }

  // Rotation function
  async function rotateImage(imageId, direction = 'right') {
    if (rotating || !data?.images?.length) return;
    const angle = direction === 'left' ? -90 : 90;
    const target = data.images.find((img) => img.id === imageId);
    if (!target) return;

    setRotating(true);
    const currentRotation = target?._localRotation || 0;
    const nextRotation = currentRotation + angle;
    const optimisticVersion = Date.now();

    // Optimistic local rotation so the UX feels instant.
    setData((prev) => ({
      ...prev,
      images: prev.images.map((img) => (
        img.id === imageId ? { ...img, _localRotation: nextRotation, _v: optimisticVersion } : img
      ))
    }));
    setLightboxImage((prev) => (
      prev?.id === imageId ? { ...prev, _localRotation: nextRotation, _v: optimisticVersion } : prev
    ));

    try {
      const response = await fetch(`/api/images/${imageId}/rotate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ angle })
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.details || `Rotation failed (${response.status})`);
      }

      const finalVersion = Date.now();
      const merged = {
        ...target,
        ...(payload && typeof payload === 'object' ? payload : {}),
        _localRotation: 0,
        _v: finalVersion
      };

      photoCache.evictImage({ id: imageId, filename: target?.filename }).catch(() => {});

      const preloadUrl = merged?.cdn_urls?.lg || merged?.cdn_urls?.md || buildVariantUrl(merged, 'lg');
      if (preloadUrl) {
        const preloadImg = new Image();
        await new Promise((resolve) => {
          preloadImg.onload = resolve;
          preloadImg.onerror = resolve;
          preloadImg.src = preloadUrl;
          setTimeout(resolve, 5000);
        });
      }

      setData((prev) => ({
        ...prev,
        images: prev.images.map((img) => (img.id === imageId ? { ...img, ...merged } : img))
      }));
      setLightboxImage((prev) => (
        prev?.id === imageId ? { ...prev, ...merged } : prev
      ));
    } catch (error) {
      console.error('Failed to rotate image:', error);
      showToast(error?.message || 'Failed to rotate image', '', 'error');
      setData((prev) => ({
        ...prev,
        images: prev.images.map((img) => (
          img.id === imageId ? { ...img, _localRotation: currentRotation, _v: Date.now() } : img
        ))
      }));
      setLightboxImage((prev) => (
        prev?.id === imageId ? { ...prev, _localRotation: currentRotation, _v: Date.now() } : prev
      ));
    } finally {
      setRotating(false);
    }
  }

  // Fullscreen functions
  function toggleFullscreen() {
    if (!lightboxRef.current) return;

    if (!document.fullscreenElement) {
      lightboxRef.current.requestFullscreen?.() ||
        lightboxRef.current.webkitRequestFullscreen?.() ||
        lightboxRef.current.mozRequestFullScreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.() ||
        document.webkitExitFullscreen?.() ||
        document.mozCancelFullScreen?.();
      setIsFullscreen(false);
    }
  }

  // Listen for fullscreen changes
  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(!!document.fullscreenElement);
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    function handleKeyDown(e) {
      if (!lightboxImage) return;
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
      if (e.key === 'Escape') closeLightbox();
    }
    
    if (lightboxImage) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [lightboxImage, lightboxIndex, data]);

  async function shareAlbumToStrip() {
    if (!data || sharingToStrip) return;
    setSharingToStrip(true);
    try {
      const response = await fetch('/api/strip/share-album', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          album_id: id,
          caption: stripShareCaption || null
        })
      });
      if (response.ok) {
        alert('Album shared to The Strip!');
        setShowStripShareModal(false);
        setStripShareCaption('');
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to share album');
      }
    } catch (error) {
      alert('Failed to share: ' + error.message);
    } finally {
      setSharingToStrip(false);
    }
  }

  if (loading || !data) {
    return (
      <section className="card">
        <div className="album-header">
          <Link to="/albums" className="album-back-button">
            <FiArrowLeft size={20} />
            <span>Back to Albums</span>
          </Link>
          <div className="album-header-content">
            <div>
              <SkeletonText lines={1} width="200px" />
              <div style={{ marginTop: '8px' }}>
                <SkeletonText lines={1} width="150px" />
              </div>
            </div>
            <div className="album-actions">
              <SkeletonText lines={1} width="80px" />
              <SkeletonText lines={1} width="100px" />
              <SkeletonText lines={1} width="100px" />
            </div>
          </div>
        </div>
        
        <div className="album-toolbar">
          <div className="album-toolbar-group left">
            <SkeletonText lines={1} width="120px" />
            <SkeletonText lines={1} width="140px" />
          </div>
        </div>
        
        <SkeletonPhotoGrid count={36} />
      </section>
    );
  }

  return (
    <PageDropZone onFilesDropped={handleFilesDropped}>
      <section className="p-4 bg-white rounded-lg shadow">
      {!editMode ? (
        <>
          {/* Album Header */}
          <div className="album-detail-header">
            <Link to="/albums" className="album-back-link">
              <FiArrowLeft size={18} />
              <span>Albums</span>
            </Link>

            <div className="album-title-row">
              <h1 className="album-title">{data.album.title}</h1>
              <span className="album-photo-count">{data.images.length} photos</span>
            </div>

            {data.album.description && (
              <p className="album-description">{data.album.description}</p>
            )}

            {/* Header Actions */}
            <div className="album-header-actions">
              <button onClick={() => setEditMode(true)} className="button-secondary">
                Edit Album
              </button>
              {data.album.title !== 'Favorites' && (
                <button onClick={deleteAlbum} className="button-danger">
                  Delete
                </button>
              )}
            </div>
          </div>

          {/* Quick Actions Bar */}
          <div className="album-quick-actions">
            <button
              onClick={() => { uploadContext?.maximizePanel(); setShowUploader(true); }}
              className="quick-action-btn primary"
            >
              <FiUpload size={18} />
              <span>Upload</span>
            </button>
            <button
              onClick={() => setShowPhotoPicker(!showPhotoPicker)}
              className="quick-action-btn"
            >
              <FiPlus size={18} />
              <span>Add Photos</span>
            </button>
            {sharesEnabled && (
              <button
                onClick={() => setShowShareModal(true)}
                className="quick-action-btn"
                title="Create and manage share links"
              >
                <FiLink size={18} />
                <span>Share</span>
              </button>
            )}
            {canUseBatchRename && data.images.length > 0 && (
              <button
                onClick={() => setShowBatchRename(true)}
                className="quick-action-btn"
              >
                <FiType size={18} />
                <span>Rename</span>
              </button>
            )}
            {stripEnabled && data.images.length > 0 && (
              <button
                onClick={() => setShowStripShareModal(true)}
                className="quick-action-btn"
                title="Share album to The Strip"
              >
                <FiShare2 size={18} />
                <span>Share to Strip</span>
              </button>
            )}
            <button
              onClick={() => setShowTeamPermissions(true)}
              className="quick-action-btn"
              title="Manage team access"
            >
              <FiUsers size={18} />
              <span>Team Access</span>
            </button>
            {data.images.length > 1 && (
              <>
                <button
                  onClick={() => setSelectionMode(!selectionMode)}
                  className={`quick-action-btn ${selectionMode ? 'active' : ''}`}
                >
                  <FiCheckSquare size={18} />
                  <span>Select</span>
                </button>
                <button
                  onClick={() => setIsRearranging(!isRearranging)}
                  className={`quick-action-btn ${isRearranging ? 'active' : ''}`}
                >
                  <FiGrid size={18} />
                  <span>Reorder</span>
                </button>
              </>
            )}
          </div>

          {/* Selection Mode Actions */}
          {selectionMode && (
            <div className="selection-toolbar">
              <span className="selection-count">{selectedImages.length} selected</span>
              <div className="selection-actions">
                <button
                  onClick={() => setShowTagSelector(true)}
                  className="button-secondary"
                  disabled={selectedImages.length === 0}
                >
                  <FiTag size={16} />
                  Tag
                </button>
                <button
                  onClick={deleteSelectedImages}
                  className="button-danger"
                  disabled={selectedImages.length === 0}
                >
                  Remove
                </button>
                <button
                  onClick={() => {
                    setSelectionMode(false);
                    setSelectedImages([]);
                  }}
                  className="button-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Rearrange Mode Actions */}
          {isRearranging && (
            <div className="rearrange-toolbar">
              <span className="rearrange-hint">Drag photos to reorder</span>
              <div className="rearrange-actions">
                <button
                  onClick={() => setOrder(o => [...o].reverse())}
                  className="button-secondary"
                >
                  <FiRefreshCw size={16} />
                  Reverse
                </button>
                <button
                  onClick={() => setIsRearranging(false)}
                  className="button-primary"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </>
        ) : (
          <div className="album-edit-form">
            <div className="album-edit-field">
              <label className="album-edit-label">Album Title</label>
              <input
                type="text"
                value={albumTitle}
                onChange={(e) => setAlbumTitle(e.target.value)}
                placeholder="Enter album title"
                className="album-title-input"
                autoFocus
              />
            </div>
            <div className="album-edit-field">
              <label className="album-edit-label">Description</label>
              <textarea
                value={albumDescription}
                onChange={(e) => setAlbumDescription(e.target.value)}
                placeholder="Add a description for this album (optional)"
                className="album-description-input"
                rows="4"
              />
            </div>
            <div className="album-edit-actions">
              <button onClick={updateAlbumInfo} className="button-primary">
                Save Changes
              </button>
              <button onClick={() => {
                setEditMode(false);
                setAlbumTitle(data.album.title);
                setAlbumDescription(data.album.description || '');
              }} className="button-secondary">
                Cancel
              </button>
            </div>
          </div>
        )}

        {showUploader && (
          <div className="album-uploader" onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); if (uploadContext?.uploading) uploadContext.minimizePanel(); else setShowUploader(false); } }} style={{display: uploadContext?.panelMinimized ? "none" : undefined}}>
            <BatchUploader
              onUploadComplete={handleUploadComplete}
              onRequestClose={() => {
                setShowUploader(false);
                setDroppedFiles([]);
              }}
              initialFiles={droppedFiles}
              albumId={id}
            />
          </div>
        )}

        {showPhotoPicker && (
          <>
            <div className="modal-backdrop" onClick={() => {
              setShowPhotoPicker(false);
              setSelectedPhotos([]);
              setPhotosPage(1);
            }} />
            <div className="photo-picker">
            <div className="photo-picker-header">
              <h3>Add Photos to Album</h3>
              <div className="photo-picker-actions">
                <span className="muted">{selectedPhotos.length} selected</span>
                <button
                  onClick={addSelectedPhotos}
                  disabled={selectedPhotos.length === 0}
                  className="button-primary"
                >
                  Add Selected
                </button>
                <button
                  onClick={() => {
                    setShowPhotoPicker(false);
                    setSelectedPhotos([]);
                    setPhotosPage(1);
                  }}
                  className="button-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
            
            {totalPhotos > 0 && (
              <div className="photo-picker-pagination-info">
                <span className="muted">
                  Showing {((photosPage - 1) * photosPerPage) + 1}-{Math.min(photosPage * photosPerPage, totalPhotos)} of {totalPhotos} photos
                </span>
              </div>
            )}
            
            <div className="photo-picker-grid">
              {allPhotos.map(photo => (
                <div
                  key={photo.id}
                  className={`photo-picker-tile ${selectedPhotos.includes(photo.id) ? 'selected' : ''}`}
                  onClick={() => togglePhotoSelection(photo.id)}
                >
                  <div className="photo-picker-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedPhotos.includes(photo.id)}
                      onChange={() => {}}
                    />
                  </div>
                  <img
                    src={buildVariantUrl(photo, 'md')}
                    alt={photo.title || 'Photo'}
                    loading="lazy"
                  />
                  <div className="photo-picker-meta">
                    <div className="photo-title">{photo.title || photo.filename}</div>
                  </div>
                </div>
              ))}
              {allPhotos.length === 0 && totalPhotos === 0 && (
                <div className="empty-state">
                  <p className="muted">No photos available to add</p>
                  <p className="muted">All your photos are already in this album or you need to upload more</p>
                </div>
              )}
            </div>
            
            {Math.ceil(totalPhotos / photosPerPage) > 1 && (
              <div className="photo-picker-pagination">
                <button 
                  onClick={() => setPhotosPage(1)}
                  disabled={photosPage === 1}
                  className="button-minimal"
                >
                  First
                </button>
                <button 
                  onClick={() => setPhotosPage(p => Math.max(1, p - 1))}
                  disabled={photosPage === 1}
                  className="button-minimal"
                >
                  Previous
                </button>
                
                <div className="pagination-pages">
                  {[...Array(Math.min(5, Math.ceil(totalPhotos / photosPerPage)))].map((_, i) => {
                    const totalPages = Math.ceil(totalPhotos / photosPerPage);
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (photosPage <= 3) {
                      pageNum = i + 1;
                    } else if (photosPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = photosPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPhotosPage(pageNum)}
                        className={`pagination-page ${photosPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button 
                  onClick={() => setPhotosPage(p => Math.min(Math.ceil(totalPhotos / photosPerPage), p + 1))}
                  disabled={photosPage === Math.ceil(totalPhotos / photosPerPage)}
                  className="button-minimal"
                >
                  Next
                </button>
                <button 
                  onClick={() => setPhotosPage(Math.ceil(totalPhotos / photosPerPage))}
                  disabled={photosPage === Math.ceil(totalPhotos / photosPerPage)}
                  className="button-minimal"
                >
                  Last
                </button>
              </div>
            )}
          </div>
          </>
        )}

        {data.images.length > 0 ? (
          <div ref={gridRef} className={`album-grid ${isRearranging ? 'rearranging' : ''}`}>
            {order.map((imageId, index) => {
              const img = data.images.find(i => i.id === imageId);
              if (!img) return null;

              return (
                <div
                  key={img.id}
                  data-image-id={img.id}
                  className={`album-tile ${draggedItem === index ? 'dragging' : ''} ${draggedOver === index ? 'drag-over' : ''} ${selectionMode && selectedImages.includes(img.id) ? 'selected' : ''}`}
                  draggable={isRearranging}
                  onDragStart={(e) => isRearranging && handleDragStart(e, index)}
                  onDragOver={(e) => isRearranging && handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => isRearranging && handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => isViewerCompact && handleTouchStart(e, index)}
                  onTouchEnd={(e) => isViewerCompact && handleTouchEnd(e, index)}
                  onClick={() => {
                    if (isDragSelecting) return;
                    if (selectionMode) {
                      toggleImageSelection(img.id);
                    } else if (!isRearranging && !isViewerCompact) {
                      openLightbox(img, index);
                    }
                  }}
                >
                  {selectionMode && (
                    <>
                      <div className="selection-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedImages.includes(img.id)}
                          onChange={() => toggleImageSelection(img.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div
                        className="drag-select-handle"
                        onMouseDown={(e) => startDrag(e, img.id, selectedImages)}
                      />
                    </>
                  )}
                  {img.processing_status === 'ready' ? (
                    <img
                      src={buildVariantUrl(img, 'md')}
                      alt={img.title || 'Photo'}
                      loading="lazy"
                    />
                  ) : img.processing_status === 'failed' ? (
                    <div className="album-tile-processing" style={{
                      width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      background: '#fef2f2', position: 'relative',
                    }}>
                      <FiX size={22} style={{ color: '#dc2626' }} />
                      <span style={{ fontSize: 11, color: '#dc2626', marginTop: 6, fontWeight: 500 }}>
                        Processing failed
                      </span>
                    </div>
                  ) : (
                    <div className="album-tile-processing" style={{
                      width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', background: img.placeholder
                        ? `url(${img.placeholder}) center/cover` : img.dominant_color || '#e5e7eb',
                      position: 'relative',
                    }}>
                      <div style={{
                        width: 24, height: 24, border: '3px solid rgba(255,255,255,0.3)',
                        borderTopColor: '#526d67', borderRadius: '50%',
                      }} />
                      <span role="status" style={{ fontSize: 12, color: '#304849', marginTop: 8, fontWeight: 600 }}>
                        {img.processing_status === 'queued' ? 'Queued for processing' : img.processing_status === 'recovering' ? 'Retrying preview' : 'Preparing preview'}
                      </span>
                      <span style={{ fontSize: 11, color: '#526d67', marginTop: 4 }}>You can keep browsing</span>
                    </div>
                  )}
                  {!isRearranging && (
                    <button
                      className={`album-tile-like ${img.is_favorite ? 'is-favorite' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLike(img.id, img.is_favorite);
                      }}
                      title={img.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <span style={{ fontSize: '16px', color: 'white' }}>
                        {img.is_favorite ? '♥' : '♡'}
                      </span>
                    </button>
                  )}
                  {img.tags && img.tags.length > 0 && (
                    <div className="album-tile-tag-indicator">
                      <FiTag size={14} />
                      <span>{img.tags.length}</span>
                    </div>
                  )}
                  {!isRearranging && (
                    <button
                      className={`album-tile-hidden-badge ${img.hidden_from_library ? 'is-hidden' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleHidden(img.id, img.hidden_from_library);
                      }}
                      title={img.hidden_from_library ? 'Show in All Photos' : 'Hide from All Photos'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {img.hidden_from_library ? (
                          <>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </>
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  )}
                  <div className="album-tile-overlay">
                    <div className="tile-info">
                      <span className="tile-number">#{index + 1}</span>
                      {img.title && <span className="tile-title">{img.title}</span>}
                    </div>
                    {!isRearranging && (
                      <button 
                        className="album-tile-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFromAlbum(img.id);
                        }}
                        title="Remove from album"
                      >
                        <span style={{ fontSize: '20px', fontWeight: 'bold', color: 'white', lineHeight: '1' }}>×</span>
                      </button>
                    )}
                  </div>
                  {isRearranging && (
                    <div className="drag-handle">
                      <span>⋮⋮</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <p className="muted">No photos in this album yet</p>
            <p className="muted">Upload photos or add existing ones to get started</p>
          </div>
        )}

        <div ref={lassoRef} className="lasso-rect" style={{ display: 'none' }} />

        {lightboxImage && (
          <div className="lightbox" ref={lightboxRef}>
            {/* Floating close button */}
            <button className="lightbox-close-floating" onClick={closeLightbox}>
              <FiX size={24} />
            </button>

            {viewMode === 'single' ? (
              <div className="lightbox-single">
	                <div className="lightbox-image-container">
                  <PhotoViewerImage
                    image={lightboxImage}
                    thumbnailVariant="md"
                    className={`lightbox-image ${zoom > 1 ? 'is-zoomed' : ''}`}
                    style={{
                      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${lightboxImage._localRotation || 0}deg)`,
                      cursor: zoom > 1 ? 'grab' : 'default',
                      transition: lightboxImage._localRotation ? 'transform 0.2s ease-out' : undefined
                    }}
                    draggable={false}
                  />

                  {showImageInfo && (
                    <div className="lightbox-info-panel">
                      <div className="info-section">
                        <h4>File Information</h4>
                        <div className="info-row">
                          <span className="info-label">Filename:</span>
                          <span className="info-value">{lightboxImage.filename}</span>
                        </div>
                        {lightboxImage.title && (
                          <div className="info-row">
                            <span className="info-label">Title:</span>
                            <span className="info-value">{lightboxImage.title}</span>
                          </div>
                        )}
                        {lightboxImage.width && lightboxImage.height && (
                          <div className="info-row">
                            <span className="info-label">Dimensions:</span>
                            <span className="info-value">{lightboxImage.width} × {lightboxImage.height}</span>
                          </div>
                        )}
                      </div>
                      {(() => { const __e = buildUiExif(lightboxImage); return __e; })() && (
                        <div className="info-section">
                          <h4>EXIF Data</h4>
                          {buildUiExif(lightboxImage)?.camera && (
                            <div className="info-row">
                              <span className="info-label">Camera:</span>
                              <span className="info-value">{buildUiExif(lightboxImage)?.camera}</span>
                            </div>
                          )}
                          {buildUiExif(lightboxImage)?.lens && (
                            <div className="info-row">
                              <span className="info-label">Lens:</span>
                              <span className="info-value">{buildUiExif(lightboxImage)?.lens}</span>
                            </div>
                          )}
                          {buildUiExif(lightboxImage)?.iso && (
                            <div className="info-row">
                              <span className="info-label">ISO:</span>
                              <span className="info-value">{buildUiExif(lightboxImage)?.iso}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="lightbox-contact-sheet">
                <div className="contact-sheet-grid">
                  {data.images.map((img, index) => (
                    <div
                      key={img.id}
                      className={`contact-sheet-item ${lightboxImage.id === img.id ? 'active' : ''}`}
                      onClick={() => {
                        setLightboxImage(img);
                        setLightboxIndex(index);
                        setViewMode('single');
                      }}
                    >
                      <span className="contact-sheet-index">#{order.indexOf(img.id) + 1}</span>
                      <img
                        src={buildVariantUrl(img, 'md')}
                        alt={img.title || 'Photo'}
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Floating Dock - matching Photos page exactly */}
            <div className="lightbox-dock">
              {/* Navigation group */}
              <div className="dock-group">
                <button
                  className="dock-btn"
                  onClick={() => navigateLightbox(-1)}
                  title="Previous photo"
                >
                  <FiChevronLeft size={22} />
                </button>
                <span className="dock-counter">
                  {lightboxIndex + 1} / {data.images.length}
                </span>
                <button
                  className="dock-btn"
                  onClick={() => navigateLightbox(1)}
                  title="Next photo"
                >
                  <FiChevronRight size={22} />
                </button>
              </div>

              <div className="dock-divider" />

              {/* View mode group */}
              <div className="dock-group">
                <button
                  className={`dock-btn ${showImageInfo ? 'active' : ''}`}
                  onClick={() => setShowImageInfo(!showImageInfo)}
                  title="Toggle info panel"
                >
                  <FiInfo size={20} />
                </button>
                <button
                  className={`dock-btn ${viewMode === 'contact' ? 'active' : ''}`}
                  onClick={() => setViewMode(viewMode === 'single' ? 'contact' : 'single')}
                  title={viewMode === 'single' ? 'Contact sheet view' : 'Single view'}
                >
                  {viewMode === 'single' ? <FiGrid size={20} /> : <FiImage size={20} />}
                </button>
              </div>

              <div className="dock-divider" />

              {/* Rotation group */}
              <div className="dock-group">
                <button
                  className="dock-btn"
                  onClick={() => rotateImage(lightboxImage.id, 'left')}
                  disabled={rotating}
                  title="Rotate left"
                >
                  <FiRotateCw size={20} className={rotating ? 'rotating' : ''} style={{ transform: 'scaleX(-1)' }} />
                </button>
                <button
                  className="dock-btn"
                  onClick={() => rotateImage(lightboxImage.id, 'right')}
                  disabled={rotating}
                  title="Rotate right"
                >
                  <FiRotateCw size={20} className={rotating ? 'rotating' : ''} />
                </button>
              </div>

              <div className="dock-divider" />

              {/* Zoom group */}
              <div className="dock-group">
                <button
                  className="dock-btn"
                  onClick={() => applyZoom(-0.25)}
                  title="Zoom out"
                >
                  <FiMinus size={20} />
                </button>
                <button
                  className="dock-btn"
                  onClick={() => applyZoom(0.25)}
                  title="Zoom in"
                >
                  <FiPlus size={20} />
                </button>
                <button
                  className="dock-btn"
                  onClick={resetZoomPan}
                  title="Reset zoom"
                >
                  <FiRefreshCw size={18} />
                </button>
              </div>

              <div className="dock-divider" />

              {/* Actions group */}
              <div className="dock-group">
                <button
                  className="dock-btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? <FiMinimize2 size={20} /> : <FiMaximize2 size={20} />}
                </button>
                {!isMobile && (
                  <a
                    className="dock-btn"
                    href={getOriginalUrl(lightboxImage, true)}
                    download
                    title="Download original"
                  >
                    <FiDownload size={20} />
                  </a>
                )}
                <button
                  className={`dock-btn ${lightboxImage?.is_favorite ? 'active favorite' : ''}`}
                  onClick={() => toggleLike(lightboxImage.id, lightboxImage.is_favorite)}
                  title={lightboxImage?.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {lightboxImage?.is_favorite ? <FaHeart size={18} /> : <FiHeart size={18} />}
                </button>
                <button
                  className="dock-btn dock-btn-danger"
                  onClick={() => {
                    if (confirm('Remove this photo from the album?')) {
                      removeFromAlbum(lightboxImage.id);
                      closeLightbox();
                    }
                  }}
                  title="Remove from album"
                >
                  <FiTrash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {showBatchRename && (
          <>
            <div className="modal-backdrop" onClick={() => setShowBatchRename(false)} />
            <div className="batch-rename-modal">
              <div className="modal-header">
                <h3>Batch Rename Photos</h3>
                <button 
                  className="modal-close-btn" 
                  onClick={() => setShowBatchRename(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-body">
                <p className="modal-description">
                  Rename all {data.images.length} photos in this album with sequential numbers.
                </p>
                
                <div className="form-group">
                  <label>Base Name</label>
                  <input
                    type="text"
                    value={basename}
                    onChange={(e) => setBasename(e.target.value)}
                    placeholder="e.g., Paramount-Gala-"
                    className="form-input"
                  />
                  <p className="form-hint">Photos will be named: {basename || 'basename-'}001, {basename || 'basename-'}002, etc.</p>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Number</label>
                    <input
                      type="number"
                      min="0"
                      value={startNumber}
                      onChange={(e) => setStartNumber(parseInt(e.target.value) || 1)}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Digit Count</label>
                    <select 
                      value={digitCount} 
                      onChange={(e) => setDigitCount(parseInt(e.target.value))}
                      className="form-input"
                    >
                      <option value="2">2 (01, 02...)</option>
                      <option value="3">3 (001, 002...)</option>
                      <option value="4">4 (0001, 0002...)</option>
                    </select>
                  </div>
                </div>
                
                <div className="preview-section">
                  <h4>Preview</h4>
                  <div className="preview-list">
                    {data.images.slice(0, 5).map((img, index) => {
                      const number = String(startNumber + index).padStart(digitCount, '0');
                      return (
                        <div key={img.id} className="preview-item">
                          <span className="preview-old">{img.title || img.filename}</span>
                          <span className="preview-arrow">→</span>
                          <span className="preview-new">{basename}{number}</span>
                        </div>
                      );
                    })}
                    {data.images.length > 5 && (
                      <div className="preview-item">
                        <span className="muted">... and {data.images.length - 5} more</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  onClick={() => setShowBatchRename(false)}
                  className="button-secondary"
                >
                  Cancel
                </button>
                <button 
                  onClick={batchRenamePhotos}
                  className="button-primary"
                  disabled={!basename}
                >
                  Rename All Photos
                </button>
              </div>
            </div>
          </>
        )}

        {showDeleteDialog && (
          <div className="modal-overlay" onClick={() => setShowDeleteDialog(false)}>
            <div className="modal-content delete-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Album</h3>
              <p className="delete-dialog-warning">
                Are you sure you want to delete the album "{data?.album?.title}"?
              </p>
              
              <div className="delete-option">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="deleteOption"
                    checked={!deletePhotosOption}
                    onChange={() => setDeletePhotosOption(false)}
                  />
                  <div>
                    <strong>Keep photos</strong>
                    <p className="muted">Remove the album but keep all photos in your library</p>
                  </div>
                </label>
              </div>
              
              <div className="delete-option">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="deleteOption"
                    checked={deletePhotosOption}
                    onChange={() => setDeletePhotosOption(true)}
                  />
                  <div>
                    <strong>Delete photos</strong>
                    <p className="muted">Delete photos that are only in this album</p>
                  </div>
                </label>
              </div>
              
              <div className="modal-footer">
                <button 
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setDeletePhotosOption(false);
                  }}
                  className="button-secondary"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDeleteAlbum}
                  className="button-danger"
                >
                  Delete Album
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
      
      {showTagSelector && (
        <TagSelectorModal
          selectedImages={selectedImages}
          onClose={() => setShowTagSelector(false)}
          onTagSelected={addTagToSelected}
          availableTags={availableTags}
          onTagsUpdated={(newTags) => setAvailableTags(newTags)}
        />
      )}
      
      {showMobileViewer && data && (
        <MobilePhotoSwiper
          images={data.images}
          thumbnailVariant="md"
          initialIndex={mobileViewerIndex}
          onClose={() => setShowMobileViewer(false)}
          onLike={toggleLike}
          allowDownload={true}
          showActions={true}
          onShare={null}
          onDelete={null}
          onRotate={null}
        />
      )}

      {/* Share Album to Strip Modal */}
      {stripEnabled && showStripShareModal && data && (
        <div className="share-modal-overlay" onClick={() => setShowStripShareModal(false)}>
          <div className="share-modal" onClick={e => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Share Album to The Strip</h3>
              <button onClick={() => setShowStripShareModal(false)} className="modal-close">
                <FiX size={20} />
              </button>
            </div>
            <div className="share-modal-content">
              <div className="share-preview album-share-preview">
                {data.images.slice(0, 4).map((img, idx) => (
                  <img
                    key={img.id}
                    src={buildVariantUrl(img, 'sm')}
                    alt={img.title || 'Album photo'}
                    className="album-preview-thumb"
                  />
                ))}
                {data.images.length > 4 && (
                  <div className="album-preview-more">+{data.images.length - 4}</div>
                )}
              </div>
              <p className="share-album-info">
                <strong>{data.album.title}</strong> - {data.images.length} photos
              </p>
              <textarea
                value={stripShareCaption}
                onChange={(e) => setStripShareCaption(e.target.value)}
                placeholder="Add a caption... (optional)"
                className="share-caption-input"
                rows={3}
              />
              <div className="share-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setShowStripShareModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={shareAlbumToStrip}
                  disabled={sharingToStrip}
                >
                  {sharingToStrip ? 'Sharing...' : 'Share Album'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Permissions Modal */}
      {showTeamPermissions && data && (
        <AlbumPermissions
          albumId={id}
          albumTitle={data.album.title}
          isTeamVisible={data.album.team_visible}
          onClose={() => setShowTeamPermissions(false)}
          onUpdate={() => loadAlbum()}
        />
      )}

      {/* Share Links Modal */}
      {sharesEnabled && showShareModal && data && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
          onClick={() => setShowShareModal(false)}
        >
          <div
            className="w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-auto bg-white rounded-t-2xl sm:rounded-xl shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - sticky on mobile */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-white border-b border-gray-200">
              <div className="min-w-0 pr-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FiLink size={18} />
                  Share Links
                </h2>
                <p className="text-sm text-gray-500 truncate">{data.album.title}</p>
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg shrink-0"
              >
                <FiX size={22} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <ShareManager albumId={id} albumTitle={data.album.title} />
            </div>
          </div>
        </div>
      )}
    </PageDropZone>
  );
}
